import type { FC } from 'react';
import { useId } from 'react';
import { Box, Divider } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import FilterDropdown from 'src/components/dashboard/chartComponents/FilterDropdown';
import ComparisonTableChart from 'src/components/dashboard/charts/ComparisonTableChart';
import { useExportOptions } from 'src/components/dashboard/charts/ComparisonTableChart/hooks';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import useWidgetFilterConfig from 'src/components/dashboard/hooks/useWidgetFilterConfig';
import { useComparisonExportData } from 'src/components/dashboard/hooks/widgetData/useComparisonExportData';
import type { StackedNumbersList } from 'src/components/followUp/common/types';
import NoResultsToShowAdjustFiltersWarning from 'src/components/followUp/common/warning/NoResultsToShowAdjustFiltersWarning';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

const ComparisonOfDemographics: FC<DashboardWidgetProps> = ({ config }) => {
  const ariaId = useId();
  const { filters, setFilters, filterConfig } = useWidgetFilterConfig(config);
  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.COMPARISON_OF_UNITS,
    config.id,
  );

  const hasValidFilter = !!filters.variable;
  const { data, isFetching, errorMsg, refetch, comparisonData, queryFilters } =
    useWidgetQuery<StackedNumbersList>(
      endpoint,
      { extraFilters: filters },
      hasValidFilter,
    );
  const comparisonExportData = useComparisonExportData(data);
  const exportOptions = useExportOptions({
    data: comparisonExportData,
    widgetName: config.name,
    queryFilters,
  });

  return (
    <WidgetFrame
      ariaId={ariaId}
      error={errorMsg}
      loading={(isFetching || !data) && hasValidFilter}
      refetch={refetch}
      exportOptions={exportOptions}
      notAffectedBy={comparisonData && 'comparison'}
      header={<WidgetHeaderWithPill title={config.name} ariaId={ariaId} />}
      footer={
        data && data.results.length === 0 ? (
          <NoResultsToShowAdjustFiltersWarning />
        ) : undefined
      }
    >
      <Box sx={{ width: '100%', overflow: 'scroll', height: '100%' }}>
        {!!filterConfig.variableFilterConfig && (
          <Box>
            <Box sx={{ px: 2.75, pb: 1.5 }}>
              <FilterDropdown
                filterConfig={filterConfig.variableFilterConfig}
                setFilters={setFilters}
                selectedFilters={filters}
              />
            </Box>
            <Divider />
          </Box>
        )}
        {!!data && <ComparisonTableChart data={data} />}
      </Box>
    </WidgetFrame>
  );
};
export default ComparisonOfDemographics;
